<template>
  <div id="application-rules__hubs">
    <div class="subheading font-weight-bold">
      {{ $t("modules.dataPath.hubs") }}
    </div>
    <v-row>
      <v-col>
        <v-select
          v-model="hubRedundancyLevel"
          :items="redundancyLevels"
          :label="$t('modules.dataPath.redundancyLevel')"
          id="application-rules__form-redundancy-level-hubs"
          :rules="[
            v =>
              (v !== null && v !== undefined) ||
              $t('validation.required', [
                $t('modules.dataPath.redundancyLevel')
              ])
          ]"
          required
        >
          <template v-slot:prepend>
            <v-tooltip color="info" top max-width="400">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">mdi-information</v-icon>
              </template>
              <div v-html="$t('modules.dataPath.redundancyInfo')"></div>
            </v-tooltip>
          </template>
        </v-select>
      </v-col>
      <v-col v-if="hubRedundancyLevel === 4">
        <v-text-field
          v-model="hubRedundancyPercentage"
          type="number"
          step="1"
          :label="$t('modules.dataPath.redundancyPercentage')"
          :hint="$t('modules.dataPath.redundancyPercentageHint')"
          id="application-rules__form-redundancy-value-hubs"
          :rules="redundancyPercentageRules"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-slider
      v-model="slider"
      class="align-center"
      :max="max"
      :min="0"
      :label="$t('modules.dataPath.reorder') + ' ' + $t('modules.dataPath.ms')"
      thumb-label
      :rules="reorderRules"
      color="primary"
      thumb-color="primary"
    >
      <template v-slot:append>
        <v-text-field
          v-model="sliderInput"
          class="mt-0 pt-0"
          single-line
          type="number"
          :max="max"
          style="width: 60px"
        ></v-text-field>
      </template>
    </v-slider>
    <v-autocomplete
      v-model="primaryHub"
      :items="primaryDevices"
      :label="$t('modules.dataPath.primaryHub')"
      id="application-rules__form-primary"
      item-text="name"
      item-value="id"
      clearable
      menu-props="offsetY"
      :rules="[
        v =>
          (v !== null && v !== undefined) ||
          $t('validation.required', [$t('modules.dataPath.primaryHub')])
      ]"
      required
    ></v-autocomplete>
    <v-autocomplete
      v-model="secondaryHub"
      :items="secondaryDevices"
      :label="$t('modules.dataPath.secondaryHub')"
      id="application-rules__form-secondary"
      item-text="name"
      item-value="id"
      required
      clearable
      menu-props="offsetY"
    ></v-autocomplete>
    <v-autocomplete
      v-model="tertiaryHub"
      :items="tertiaryDevices"
      :label="$t('modules.dataPath.tertiaryHub')"
      id="application-rules__form-tertiary"
      item-text="name"
      item-value="id"
      required
      clearable
      menu-props="offsetY"
    ></v-autocomplete>
  </div>
</template>

<script>
export default {
  name: "ApplicationRuleHubs",
  data() {
    return {
      isLoading: false,
      slider: 0,
      sliderInput: 0,
      max: 1000,
      minRedundancyPercentage: 0,
      maxRedundancyPercentage: 1000,
      redundancyLevels: [
        { value: 0, text: this.$t("modules.dataPath.none") },
        { value: 1, text: this.$t("modules.dataPath.double") },
        { value: 2, text: this.$t("modules.dataPath.triple") },
        { value: 3, text: this.$t("modules.dataPath.quadruple") },
        { value: 4, text: this.$t("modules.dataPath.custom") }
      ],
      redundancyPercentageRules: [
        v =>
          !!v ||
          this.$t("validation.required", [
            this.$t("modules.dataPath.redundancyPercentage")
          ]),
        v =>
          (Number.isInteger(Number(v)) &&
            v >= this.minRedundancyPercentage &&
            v <= this.maxRedundancyPercentage) ||
          this.$t("validation.invalid", [
            this.$t("modules.dataPath.redundancyPercentage")
          ])
      ],
      reorderRules: [
        v => {
          if (
            this.sliderInput > this.max ||
            this.sliderInput < 0 ||
            (v > 0 && v < 5)
          ) {
            return this.$t("modules.dataPath.reorderRange", [
              "5",
              this.max.toString()
            ]);
          } else {
            return true;
          }
        }
      ]
    };
  },

  watch: {
    slider(val) {
      if (val < 0) {
        this.hubReorder = 0;
        this.sliderInput = 0;
      } else if (val > this.max) {
        this.hubReorder = this.max;
      } else {
        this.hubReorder = val;
        this.sliderInput = val;
      }
    },

    sliderInput(val) {
      if (val < 0) {
        this.slider = 0;
      } else if (val > this.max) {
        this.slider = this.max;
      } else {
        this.slider = val;
      }
    }
  },

  created() {
    this.slider = this.hubReorder;
  },

  computed: {
    devices() {
      return this.$store.state.devices.devices;
    },

    hubRedundancyLevel: {
      get() {
        return this.$store.state.dataPath.editedItem.hubRedundancyLevel;
      },
      set(val) {
        this.$store.commit("dataPath/updateHubRedundancyLevel", val);
      }
    },

    hubRedundancyPercentage: {
      get() {
        return this.$store.state.dataPath.editedItem.hubRedundancyPercentage;
      },
      set(val) {
        this.$store.commit("dataPath/updateHubRedundancyPercentage", val);
      }
    },

    hubReorder: {
      get() {
        return this.$store.state.dataPath.editedItem.hubReorder;
      },
      set(val) {
        this.$store.commit("dataPath/updateHubReorder", val);
      }
    },

    primaryHub: {
      get() {
        return this.$store.state.dataPath.editedItem.primaryHub;
      },
      set(val) {
        this.$store.commit("dataPath/updatePrimaryHub", val);
      }
    },

    secondaryHub: {
      get() {
        return this.$store.state.dataPath.editedItem.secondaryHub;
      },
      set(val) {
        this.$store.commit("dataPath/updateSecondaryHub", val);
      }
    },

    tertiaryHub: {
      get() {
        return this.$store.state.dataPath.editedItem.tertiaryHub;
      },
      set(val) {
        this.$store.commit("dataPath/updateTertiaryHub", val);
      }
    },

    hosts() {
      return this.$store.state.dataPath.editedItem.hosts;
    },

    primaryDevices() {
      return this.devices.filter(data => {
        return (
          data.id !== this.secondaryHub &&
          data.id !== this.tertiaryHub &&
          data.ip_addresses.length > 0 &&
          this.hosts.indexOf(data.id) === -1
        );
      });
    },

    secondaryDevices() {
      return this.devices.filter(data => {
        return (
          data.id !== this.primaryHub &&
          data.id !== this.tertiaryHub &&
          data.ip_addresses.length > 0 &&
          this.hosts.indexOf(data.id) === -1
        );
      });
    },

    tertiaryDevices() {
      return this.devices.filter(data => {
        return (
          data.id !== this.primaryHub &&
          data.id !== this.secondaryHub &&
          data.is_server === true &&
          this.hosts.indexOf(data.id) === -1
        );
      });
    }
  }
};
</script>
